import { SliderComponentCss, SliderComponentHtml } from './SliderComponent.tpl.js';

/**
 * Кастомный слайдер (одиночный или диапазон) без зависимостей.
 * Поддержка range, vertical, disabled, событий input/change.
 */
class SliderComponent extends HTMLElement {
    // Перечень отслеживаемых атрибутов
    static get observedAttributes() {
        return ['data-value', 'min', 'max', 'step', 'disabled', 'data-values', 'range', 'vertical', 'show-value', 'show-bounds', 'min-label', 'max-label', 'null-bounds'];
    }
    constructor() {
        super();
        this.min = 0;
        this.max = 100;
        this.step = 1;
        this.values = [0]; // [left, right] для диапазона, [value] для одиночного
        this.draggingHandle = null;
        this.shadow = this.attachShadow({ mode: 'open' });
        this.render();
    }
    connectedCallback() {
        this._parseAttributes();
        this._update();
        this._addEvents();
    }
    attributeChangedCallback() {
        this._parseAttributes();
        this._update();
    }
    /**
     * Читает значения атрибутов и обновляет состояние
     */
    _parseAttributes() {
        this.min = +(this.getAttribute('min') ?? 0);
        this.max = +(this.getAttribute('max') ?? 100);
        this.step = +(this.getAttribute('step') ?? 1);
        if (this.hasAttribute('range')) {
            // Диапазон значений (пример: "10,90")
            const vals = (this.getAttribute('data-values') || '').split(',').map(v => +v.trim());
            this.values = [
                isNaN(vals[0]) ? this.min : Math.max(this.min, Math.min(this.max, vals[0])),
                isNaN(vals[1]) ? this.max : Math.max(this.min, Math.min(this.max, vals[1]))
            ];
        }
        else {
            // Одиночное значение
            let v = +(this.getAttribute('data-value') ?? this.min);
            this.values = [Math.max(this.min, Math.min(this.max, v))];
        }
    }
    /** Позволяет менять value программно */
    set valueAttr(val) { this.setAttribute('data-value', String(val)); }
    get valueAttr() { return this.values[0]; }
    /** Позволяет менять values для диапазона программно */
    set valuesAttr(val) { this.setAttribute('data-values', this._formatRangeAttributeValue(this._toInternalRangeValues(val))); }
    get valuesAttr() { return this._getPublicRangeValues(); }
    /**
     * Навешивает обработчики событий для drag, клик и клавиатуры
     */
    _addEvents() {
        const handles = [
            this.shadow.querySelector('.handle-0'),
            this.shadow.querySelector('.handle-1')
        ];
        const track = this.shadow.querySelector('.slider-track');
        // Drag + клавиатура для левого/единственного хэндлера
        if (handles[0]) {
            handles[0].addEventListener('mousedown', e => this._startDrag(e, 0));
            handles[0].addEventListener('touchstart', e => this._startDrag(e, 0));
            handles[0].addEventListener('keydown', (e) => {
                let v = this.values[0];
                if (e.key === 'ArrowLeft' || e.key === 'ArrowDown')
                    v -= this.step;
                if (e.key === 'ArrowRight' || e.key === 'ArrowUp')
                    v += this.step;
                this._setValue(0, v, true);
                e.preventDefault();
            });
        }
        // Drag + клавиатура для правого хэндлера (range)
        if (handles[1]) {
            handles[1].addEventListener('mousedown', e => this._startDrag(e, 1));
            handles[1].addEventListener('touchstart', e => this._startDrag(e, 1));
            handles[1].addEventListener('keydown', (e) => {
                let v = this.values[1];
                if (e.key === 'ArrowLeft' || e.key === 'ArrowDown')
                    v -= this.step;
                if (e.key === 'ArrowRight' || e.key === 'ArrowUp')
                    v += this.step;
                this._setValue(1, v, true);
                e.preventDefault();
            });
        }
        // Клик по треку
        if (track) {
            track.addEventListener('click', e => this._jumpTo(e));
        }
    }
    /**
     * Старт перетаскивания (mouse или touch)
     */
    _startDrag(e, handleIdx) {
        if (this.hasAttribute('disabled'))
            return;
        this.draggingHandle = handleIdx;
        const moveHandler = (ev) => this._onDrag(ev);
        const upHandler = () => {
            this.draggingHandle = null;
            window.removeEventListener('mousemove', moveHandler);
            window.removeEventListener('touchmove', moveHandler);
            window.removeEventListener('mouseup', upHandler);
            window.removeEventListener('touchend', upHandler);
            // Событие "change" при окончании drag
            this._fireChange();
        };
        window.addEventListener('mousemove', moveHandler);
        window.addEventListener('touchmove', moveHandler);
        window.addEventListener('mouseup', upHandler);
        window.addEventListener('touchend', upHandler);
        e.preventDefault();
    }
    /**
     * Обработка перетаскивания handle (mouse/touch move)
     */
    _onDrag(e) {
        if (this.draggingHandle == null)
            return;
        let pos;
        const vertical = this.hasAttribute('vertical');
        const track = this.shadow.querySelector('.slider-track');
        const rect = track.getBoundingClientRect();
        if (vertical) {
            let clientY = e.clientY;
            if (e.touches) {
                clientY = e.touches[0].clientY;
            }
            let percent = 1 - (clientY - rect.top) / rect.height;
            percent = Math.max(0, Math.min(1, percent));
            pos = this.min + percent * (this.max - this.min);
        }
        else {
            let clientX = e.clientX;
            if (e.touches) {
                clientX = e.touches[0].clientX;
            }
            let percent = (clientX - rect.left) / rect.width;
            percent = Math.max(0, Math.min(1, percent));
            pos = this.min + percent * (this.max - this.min);
        }
        const stepped = Math.round((pos - this.min) / this.step) * this.step + this.min;
        this._setValue(this.draggingHandle, stepped, false);
    }
    /**
     * Клик по треку (устанавливает ближайший хэндлер)
     */
    _jumpTo(e) {
        if (this.hasAttribute('disabled'))
            return;
        const track = this.shadow.querySelector('.slider-track');
        const rect = track.getBoundingClientRect();
        let pos;
        const vertical = this.hasAttribute('vertical');
        if (vertical) {
            let percent = 1 - (e.clientY - rect.top) / rect.height;
            percent = Math.max(0, Math.min(1, percent));
            pos = this.min + percent * (this.max - this.min);
        }
        else {
            let percent = (e.clientX - rect.left) / rect.width;
            percent = Math.max(0, Math.min(1, percent));
            pos = this.min + percent * (this.max - this.min);
        }
        let idx = 0;
        if (this.hasAttribute('range')) {
            // К ближайшему хэндлеру
            const d0 = Math.abs(pos - this.values[0]);
            const d1 = Math.abs(pos - this.values[1]);
            idx = d1 < d0 ? 1 : 0;
        }
        const stepped = Math.round((pos - this.min) / this.step) * this.step + this.min;
        this._setValue(idx, stepped, false);
        this._fireChange();
    }
    /**
     * Устанавливает значение для нужного handle, эмитит события input/change
     */
    _setValue(idx, val, keyboard = false) {
        val = Math.min(this.max, Math.max(this.min, val));
        if (this.hasAttribute('range')) {
            // Для range: left <= right
            if (idx === 0)
                val = Math.min(val, this.values[1]);
            else
                val = Math.max(val, this.values[0]);
            if (val === this.values[idx])
                return;
            this.values[idx] = val;
            this.setAttribute('data-values', this._formatRangeAttributeValue());
            this._update();
            // Событие input для range
            this.dispatchEvent(new CustomEvent('input', { detail: { values: this._getPublicRangeValues(), handle: idx }, bubbles: true }));
        }
        else {
            if (val === this.values[0])
                return;
            this.values[0] = val;
            this.setAttribute('data-value', String(val));
            this._update();
            // Событие input для одиночного
            this.dispatchEvent(new CustomEvent('input', { detail: { value: val }, bubbles: true }));
        }
        if (keyboard)
            this._fireChange();
    }
    /**
     * Эмитит событие change (при отпускании мыши)
     */
    _fireChange() {
        if (this.hasAttribute('range')) {
            this.dispatchEvent(new CustomEvent('change', { detail: { values: this._getPublicRangeValues() }, bubbles: true }));
        }
        else {
            this.dispatchEvent(new CustomEvent('change', { detail: { value: this.values[0] }, bubbles: true }));
        }
    }
    _getPublicRangeValues() {
        return [
            this._getPublicRangeValue(0, this.values[0]),
            this._getPublicRangeValue(1, this.values[1])
        ];
    }
    _getPublicRangeValue(idx, value) {
        if (!this.hasAttribute('null-bounds')) {
            return value;
        }
        if (idx === 0 && value === this.min) {
            return null;
        }
        if (idx === 1 && value === this.max) {
            return null;
        }
        return value;
    }
    _toInternalRangeValues(values) {
        return [
            values[0] === null ? this.min : values[0],
            values[1] === null ? this.max : values[1]
        ];
    }
    _formatRangeAttributeValue(values = this.values) {
        if (!this.hasAttribute('null-bounds')) {
            return values.join(',');
        }
        return [
            values[0] === this.min ? 'null' : String(values[0]),
            values[1] === this.max ? 'null' : String(values[1])
        ].join(',');
    }
    /**
     * Обновляет позицию handle'ов и заполнения
     */
    _update() {
        const vertical = this.hasAttribute('vertical');
        const handles = [
            this.shadow.querySelector('.handle-0'),
            this.shadow.querySelector('.handle-1')
        ];
        const valueLabels = [
            this.shadow.querySelector('.value-0'),
            this.shadow.querySelector('.value-1')
        ];
        const filled = this.shadow.querySelector('.slider-filled');
        const showValue = this.hasAttribute('show-value');
        if (this.hasAttribute('range')) {
            if (handles[1])
                handles[1].style.display = '';
            const percent0 = ((this.values[0] - this.min) / (this.max - this.min)) * 100;
            const percent1 = ((this.values[1] - this.min) / (this.max - this.min)) * 100;
            if (vertical) {
                if (handles[0]) {
                    handles[0].style.top = `${100 - percent0}%`;
                    handles[0].style.left = '50%';
                }
                if (handles[1]) {
                    handles[1].style.top = `${100 - percent1}%`;
                    handles[1].style.left = '50%';
                }
                if (valueLabels[0]) {
                    valueLabels[0].style.top = `${100 - percent0}%`;
                    valueLabels[0].style.left = '50%';
                }
                if (valueLabels[1]) {
                    valueLabels[1].style.top = `${100 - percent1}%`;
                    valueLabels[1].style.left = '50%';
                }
                if (filled) {
                    filled.style.bottom = `${percent0}%`;
                    filled.style.top = `${100 - percent1}%`;
                    filled.style.height = `${percent1 - percent0}%`;
                }
            }
            else {
                if (handles[0])
                    handles[0].style.left = `${percent0}%`;
                if (handles[1])
                    handles[1].style.left = `${percent1}%`;
                if (valueLabels[0])
                    valueLabels[0].style.left = `${percent0}%`;
                if (valueLabels[1])
                    valueLabels[1].style.left = `${percent1}%`;
                if (filled) {
                    filled.style.left = `${percent0}%`;
                    filled.style.width = `${percent1 - percent0}%`;
                }
            }
        }
        else {
            if (handles[1])
                handles[1].style.display = 'none';
            const percent = ((this.values[0] - this.min) / (this.max - this.min)) * 100;
            if (vertical) {
                if (handles[0]) {
                    handles[0].style.top = `${100 - percent}%`;
                    handles[0].style.left = '50%';
                }
                if (valueLabels[0]) {
                    valueLabels[0].style.top = `${100 - percent}%`;
                    valueLabels[0].style.left = '50%';
                }
                if (filled) {
                    filled.style.bottom = '0';
                    filled.style.top = `${100 - percent}%`;
                    filled.style.height = `${percent}%`;
                }
            }
            else {
                if (handles[0])
                    handles[0].style.left = `${percent}%`;
                if (valueLabels[0])
                    valueLabels[0].style.left = `${percent}%`;
                if (filled) {
                    filled.style.left = '0';
                    filled.style.width = `${percent}%`;
                }
            }
        }
        // Disabled state
        const dis = this.hasAttribute('disabled');
        handles.forEach(h => { if (h)
            h.tabIndex = dis ? -1 : 0; });
        if (this.shadow.querySelector('.slider-track')) {
            this.shadow.querySelector('.slider-track').style.pointerEvents = dis ? 'none' : '';
        }
        if (valueLabels[0]) {
            valueLabels[0].textContent = String(this.values[0]);
            valueLabels[0].style.display = showValue ? '' : 'none';
        }
        if (valueLabels[1]) {
            valueLabels[1].textContent = String(this.values[1] ?? '');
            // Когда оба значения совпадают (булеты слились в одну точку),
            // прячем правый лейбл — достаточно показать одно значение.
            const sameValue = this.hasAttribute('range') && this.values[0] === this.values[1];
            valueLabels[1].style.display = showValue && this.hasAttribute('range') && !sameValue ? '' : 'none';
        }
        // Граничные подписи (min/max). Текст можно перебить атрибутами min-label/max-label,
        // иначе показываются числовые границы. В вертикальном режиме отключены.
        const showBounds = this.hasAttribute('show-bounds') && !vertical;
        const boundMin = this.shadow.querySelector('.bound-min');
        const boundMax = this.shadow.querySelector('.bound-max');
        if (boundMin) {
            boundMin.textContent = this.getAttribute('min-label') ?? String(this.min);
            boundMin.style.display = showBounds ? '' : 'none';
        }
        if (boundMax) {
            boundMax.textContent = this.getAttribute('max-label') ?? String(this.max);
            boundMax.style.display = showBounds ? '' : 'none';
        }
        this._resolveValueLabelOverlap(valueLabels);
    }
    /**
     * Разруливает накладку подписей значений в горизонтальном range-режиме.
     * Если расстояние между центрами меньше (w0 + w1)/2 + gap, симметрично разводит лейблы
     * через CSS-переменную --slider-value-shift. Минимальный зазор задаётся
     * переменной --slider-value-gap (по умолчанию 8px).
     *
     * Расчёт идёт через offsetWidth и проценты — без getBoundingClientRect,
     * чтобы измерения не зависели от текущего значения transform (transition).
     */
    _resolveValueLabelOverlap(valueLabels) {
        const label0 = valueLabels[0];
        const label1 = valueLabels[1];
        if (!label0 || !label1)
            return;
        if (!this.hasAttribute('range') || !this.hasAttribute('show-value'))
            return;
        if (this.hasAttribute('vertical'))
            return;
        // Стартовое состояние: предыдущие сдвиги/«скрытия по налезанию» сбрасываем,
        // дальше повторно применяем по результатам расчёта.
        label0.style.removeProperty('--slider-value-shift');
        label1.style.removeProperty('--slider-value-shift');
        // Если _update уже скрыл лейблы по show-value/range — выходим.
        if (label0.style.display === 'none' && label1.style.display === 'none')
            return;
        const track = this.shadow.querySelector('.slider-track');
        const trackWidth = track?.offsetWidth ?? 0;
        if (trackWidth === 0)
            return;
        const range = this.max - this.min;
        if (range <= 0)
            return;
        const gapRaw = getComputedStyle(this).getPropertyValue('--slider-value-gap').trim();
        const gap = gapRaw ? parseFloat(gapRaw) : 8;
        const center0 = ((this.values[0] - this.min) / range) * trackWidth;
        const center1 = ((this.values[1] - this.min) / range) * trackWidth;
        // Сначала восстановим базовую видимость от _update — нам важно мерять реальные ширины.
        // Если _update поставил display='none', оставляем как есть.
        const w0 = label0.style.display === 'none' ? 0 : label0.offsetWidth;
        const w1 = label1.style.display === 'none' ? 0 : label1.offsetWidth;
        // Налезание плавающего значения на статичную границу → прячем границу,
        // плавающее значение остаётся видимым, чтобы пользователь всегда видел текущий выбор.
        // Берём именно крайние видимые лейблы — иначе при слипшихся значениях
        // (label1 скрыт, оба ползунка на max) проверка bound-max ничего не увидит.
        const showBounds = this.hasAttribute('show-bounds');
        if (showBounds) {
            const candidates = [];
            if (label0.style.display !== 'none' && w0 > 0)
                candidates.push({ center: center0, width: w0 });
            if (label1.style.display !== 'none' && w1 > 0)
                candidates.push({ center: center1, width: w1 });
            if (candidates.length > 0) {
                const leftmost = candidates.reduce((a, b) => (a.center <= b.center ? a : b));
                const rightmost = candidates.reduce((a, b) => (a.center >= b.center ? a : b));
                const boundMin = this.shadow.querySelector('.bound-min');
                const boundMinWidth = boundMin && boundMin.style.display !== 'none' ? boundMin.offsetWidth : 0;
                if (boundMin && boundMinWidth > 0 && leftmost.center - leftmost.width / 2 < boundMinWidth + gap) {
                    boundMin.style.display = 'none';
                }
                const boundMax = this.shadow.querySelector('.bound-max');
                const boundMaxWidth = boundMax && boundMax.style.display !== 'none' ? boundMax.offsetWidth : 0;
                if (boundMax && boundMaxWidth > 0 && rightmost.center + rightmost.width / 2 > trackWidth - boundMaxWidth - gap) {
                    boundMax.style.display = 'none';
                }
            }
        }
        // Сдвиг плавающих относительно друг друга — только если оба ещё видимы.
        if (label0.style.display !== 'none' && label1.style.display !== 'none' && w0 > 0 && w1 > 0) {
            const distance = center1 - center0;
            const required = (w0 + w1) / 2 + gap;
            if (distance < required) {
                const shift = (required - distance) / 2;
                label0.style.setProperty('--slider-value-shift', `${-shift}px`);
                label1.style.setProperty('--slider-value-shift', `${shift}px`);
            }
        }
    }
    /**
     * Рендер шаблона и стилей
     */
    render() {
        this.shadow.innerHTML = `<style>${SliderComponentCss}</style>${SliderComponentHtml}`;
    }
}

export { SliderComponent };
